import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Col, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { Btn } from "../../../../AbstractElements";
import CommonCardHeader from "../../../../Utils/CommonComponents/CommonCardHeader";
import { baseUrl } from "../../../../Utils/Baseurl/Baseurl";
import dayjs from "dayjs";
import api from "../../../../services/api";
import { toast } from "react-toastify";

// Define interface for token counts
interface TokenCount {
    id: number;
    tokencount: string;
}

export default function WeeklyTokens() {
    const [tokenCounts, setTokenCounts] = useState<TokenCount[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [newTokenCount, setNewTokenCount] = useState("");
    const [editTokenCount, setEditTokenCount] = useState("");
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [upcomingDate, setUpcomingDate] = useState(""); // State for next available date
    const [assignedDates, setAssignedDates] = useState<string[]>([]);

    useEffect(() => {
        fetchAssignedDates();
        fetchTokenCounts();
    }, []);

    // Fetch assigned dates and determine the next upcoming date
    const fetchAssignedDates = async () => {
        try {
            const response = await api.get(`${baseUrl}assignedDates/getAllDates`);
            console.log("Fetched Assigned Dates:", response.data);
    
            // Extract and parse dates from response
            const extractedDates: dayjs.Dayjs[] = response.data.map((item: { dates: string }) =>
                dayjs(item.dates).startOf("day") // Normalize to remove time component
            );
    
            // Sort dates in ascending order
            const sortedDates = extractedDates.sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => a.valueOf() - b.valueOf());
    
            // Get today's date
            const today = dayjs().startOf("day");
    
            // Find the next upcoming date
            const nextDate = sortedDates.find((date: dayjs.Dayjs) => date.isAfter(today));
    
            if (nextDate) {
                setUpcomingDate(nextDate.format("DD/MM/YYYY")); // Format as needed
            } else {
                setUpcomingDate("No upcoming dates available");
            }
        } catch (error) {
            console.error("Failed to fetch assigned dates:", error);
            toast.error("Failed to fetch assigned dates.");
        }
    };
    
    

    // Fetch token counts
    const fetchTokenCounts = async () => {
        try {
            const res = await axios.get(`${baseUrl}generalsettings/tokencounts`);
            setTokenCounts(res.data);
        } catch (error) {
            console.error("Error fetching token counts:", error);
        }
    };

    // Add new token count
    const addTokenCount = async () => {
        try {
            await axios.post(`${baseUrl}generalsettings/createtokencount`, {
                tokencount: newTokenCount,
            });
            fetchTokenCounts();
            setModalOpen(false);
            setNewTokenCount("");
        } catch (error) {
            console.error("Error adding token count:", error);
        }
    };

    // Open update modal with selected token count
    const openUpdateModal = (id: number, count: string) => {
        setSelectedId(id);
        setEditTokenCount(count);
        setUpdateModalOpen(true);
    };

    // Update token count
    const updateTokenCount = async () => {
        if (selectedId === null) return;

        try {
            await axios.post(`${baseUrl}generalsettings/updatetokencount`, {
                id: selectedId,
                tokencount: editTokenCount,
            });
            fetchTokenCounts();
            setUpdateModalOpen(false);
        } catch (error) {
            console.error("Error updating token count:", error);
        }
    };

    return (
        <Col sm={12}>
            <Card>
                <CommonCardHeader title={`Token Count for ${upcomingDate }`} />
                {tokenCounts.length === 0 && (
                    <div className="d-flex justify-content-end p-3">
                        <Btn color="primary" onClick={() => setModalOpen(true)}>Add</Btn>
                    </div>
                )}
                <div className="table-responsive">
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Token Count</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokenCounts.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>{item.tokencount}</td>
                                    <td>
                                        <Btn color="warning" onClick={() => openUpdateModal(item.id, item.tokencount)}>Update</Btn>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* Add Token Count Modal */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>Add Token Count</ModalHeader>
                <ModalBody>
                    <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Token Count"
                        value={newTokenCount}
                        onChange={(e) => setNewTokenCount(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Btn color="success" onClick={addTokenCount}>Add</Btn>
                    <Btn color="secondary" onClick={() => setModalOpen(false)}>Cancel</Btn>
                </ModalFooter>
            </Modal>

            {/* Update Token Count Modal */}
            <Modal isOpen={updateModalOpen} toggle={() => setUpdateModalOpen(false)}>
                <ModalHeader toggle={() => setUpdateModalOpen(false)}>Update Token Count</ModalHeader>
                <ModalBody>
                    <input
                        type="number"
                        className="form-control"
                        value={editTokenCount}
                        onChange={(e) => setEditTokenCount(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Btn color="primary" onClick={updateTokenCount}>Update</Btn>
                    <Btn color="secondary" onClick={() => setUpdateModalOpen(false)}>Cancel</Btn>
                </ModalFooter>
            </Modal>
        </Col>
    );
}
